import { Box, Divider, Stack, Typography } from "@mui/material";
import pluralize from "pluralize";
import { ReactNode } from "react";

import { formatPounds } from "./episodicDisplay";
import { ContainerEventSummary } from "./eventContainerRows";

function Figure({
  label,
  value,
  caption,
}: {
  label: ReactNode;
  value: ReactNode;
  caption: string;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      {value}
      <Typography variant="caption" color="text.secondary" component="div">
        {caption}
      </Typography>
    </Box>
  );
}

function withUnweighed(pounds: number, unweighed: number): string {
  return unweighed > 0 ? `${formatPounds(pounds)} + ${unweighed} unweighed` : formatPounds(pounds);
}

export type ManifestsState = "loading" | "error" | "loaded";

const MANIFESTS_PENDING_CAPTION: Record<Exclude<ManifestsState, "loaded">, string> = {
  loading: "Loading manifests…",
  error: "The manifests could not be loaded.",
};

function manifestedCaption({
  manifestCount,
  enteredStreamCount,
  awaitingManifestCount,
}: ContainerEventSummary): string {
  const parts = [`from ${pluralize("linked manifest", manifestCount, true)}`];
  if (enteredStreamCount > 0) {
    const uses = enteredStreamCount === 1 ? "uses" : "use";
    parts.push(`${pluralize("stream", enteredStreamCount, true)} ${uses} entered pounds`);
  }
  if (awaitingManifestCount > 0) {
    parts.push(`${pluralize("container", awaitingManifestCount, true)} awaiting a manifest`);
  }
  return parts.join(" · ");
}

export function EventSummaryStrip({
  summary,
  manifests,
  showManifested,
  reliefApplies,
}: {
  summary: ContainerEventSummary;
  manifests: ManifestsState;
  showManifested: boolean;
  /** False where the card shows the relief advisory: the waste is then recorded, not exempt. */
  reliefApplies: boolean;
}) {
  const { estimatedLb } = summary;
  return (
    <Box
      role="group"
      aria-label="Event pounds"
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: "grey.50",
      }}
    >
      <Stack direction="row" flexWrap="wrap" columnGap={4} rowGap={2} useFlexGap>
        {estimatedLb != null && (
          <Figure
            label="Estimated"
            value={
              <Typography variant="h5" color="text.secondary">
                {formatPounds(estimatedLb)}
              </Typography>
            }
            caption="from your Form 8700-12 notification"
          />
        )}
        <Figure
          label="Generated"
          value={
            <Typography variant="h5">
              {withUnweighed(summary.generatedLb, summary.unweighedCount)}
            </Typography>
          }
          caption={
            reliefApplies
              ? "from container fill records · exempt from generator status"
              : "from container fill records · record only in this state"
          }
        />
        {showManifested && (
          <>
            <Divider orientation="vertical" flexItem />
            <Figure
              label="Manifested"
              value={
                <Typography variant="h5">
                  {manifests === "loaded"
                    ? withUnweighed(summary.manifestedLb, summary.manifestedUnweighedCount)
                    : "—"}
                </Typography>
              }
              caption={
                manifests === "loaded"
                  ? manifestedCaption(summary)
                  : MANIFESTS_PENDING_CAPTION[manifests]
              }
            />
          </>
        )}
      </Stack>
    </Box>
  );
}
